import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Checkpoint, Exercise, LessonResponse } from '@pen/shared';
import { api } from '../api';
import { Loading, message, ResourceError, Rich, useResource } from '../ui/hooks';
import { EmbedSection, GuessSection, PeleDiagram } from './Sections';
import { pauseEmbeds } from './embed';
import type { ActProgress } from './embed';
import { PracticeLayer } from './PracticeLayer';
import { VideoSection } from './VideoSection';
import { FilmSection, type FilmBridge, type FilmHandle } from './FilmSection';
import { filmsBefore, reachedFilmCheckpoints, type FilmLink } from './film';

export function LessonPage() {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  const resource = useResource(() => api.lesson(id), id);
  if (resource.error) return <main className="page"><ResourceError error={resource.error} retry={resource.reload} /></main>;
  if (!resource.data) return <Loading />;
  return <LessonPlayer key={id} data={resource.data} format={search.get('format') ?? 'scroll'} reviewId={search.get('review')} reviewCheckpoint={search.get('checkpoint')} exerciseId={search.get('exercise')} />;
}

export function LessonPlayer({ data, format = 'scroll', reviewId = null, reviewCheckpoint = null, exerciseId = null }: { data: LessonResponse; format?: string; reviewId?: string | null; reviewCheckpoint?: string | null; exerciseId?: string | null }) {
  const { lesson } = data;
  const navigate = useNavigate();
  const root = useRef<HTMLElement>(null);
  const [unlocked, setUnlocked] = useState(() => new Set(data.progress.completedCheckpoints));
  const [completed, setCompleted] = useState(() => new Set(data.progress.completedCheckpoints));
  const [active, setActive] = useState<Checkpoint | null>(null);
  const [activeExercise, setActiveExercise] = useState<Exercise | null>(null);
  const [stream, setStream] = useState<Exercise[]>([]);
  const [starting, setStarting] = useState(false), [streamError, setStreamError] = useState<string | null>(null);
  const [progressError, setProgressError] = useState<string | null>(null);
  const lastProgress = useRef(-1);
  const pendingProgress = useRef<string | null>(null);
  const progressInFlight = useRef(false);
  const autoOpened = useRef(false);
  const filmBefore = filmsBefore(lesson.sections);
  const films = useRef(new Map<string, FilmHandle>());
  const [filmLinks, setFilmLinks] = useState<Record<string, FilmLink[]>>({});
  const [nowPlaying, setNowPlaying] = useState<{ filmId: string; index: number } | null>(null);
  const [stopped, setStopped] = useState<{ filmId: string; checkpointId: string } | null>(null);
  const report = useCallback((index: number) => {
    if (index <= lastProgress.current) return;
    lastProgress.current = index;
    pendingProgress.current = lesson.sections[index]!.id;
    async function send() {
      if (progressInFlight.current) return;
      progressInFlight.current = true;
      try {
        while (pendingProgress.current) {
          const section = pendingProgress.current;
          pendingProgress.current = null;
          await api.progress(lesson.id, section);
        }
        setProgressError(null);
      } catch (reason) { setProgressError(message(reason)); lastProgress.current = -1; }
      finally { progressInFlight.current = false; }
    }
    void send();
  }, [lesson]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const index = Number((entry.target as HTMLElement).dataset.index);
        if (lesson.sections[index]?.kind !== 'checkpoint') report(index);
      }
    }, { threshold: .15 });
    root.current?.querySelectorAll('[data-index]').forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [lesson, report]);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const next: string[] = [];
      for (let i = 0; i < lesson.sections.length; i++) {
        const section = lesson.sections[i]!;
        if (section.kind !== 'checkpoint' || filmBefore[section.id]) continue;
        const previous = i === 0 ? root.current?.querySelector('.lesson-header') : document.getElementById(lesson.sections[i - 1]!.id);
        if (previous && previous.getBoundingClientRect().bottom <= window.innerHeight * .85) next.push(section.checkpoint.id);
      }
      if (next.length) setUnlocked(current => {
        if (next.every(id => current.has(id))) return current;
        return new Set([...current, ...next]);
      });
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(check); };
    check(); window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(frame); };
  }, [lesson]);
  useEffect(() => {
    if (!reviewId || !reviewCheckpoint || autoOpened.current) return;
    const section = lesson.sections.find(section => section.kind === 'checkpoint' && section.checkpoint.id === reviewCheckpoint);
    if (section?.kind === 'checkpoint' && section.checkpoint.kind === 'redraw') { autoOpened.current = true; setActive(section.checkpoint); }
  }, [lesson, reviewId, reviewCheckpoint]);
  useEffect(() => {
    if (!active) return;
    if (root.current) pauseEmbeds(root.current);
  }, [active]);
  const loadStream = useCallback(() => { api.stream(lesson.id).then(data => setStream(data.exercises), () => undefined); }, [lesson.id]);
  useEffect(loadStream, [loadStream]);
  useEffect(() => {
    if (!exerciseId || autoOpened.current) return;
    autoOpened.current = true;
    api.exercise(exerciseId).then(exercise => { if (exercise.lessonId === lesson.id) openExercise(exercise); }, reason => setStreamError(message(reason)));
  }, [exerciseId, lesson.id]);
  function openExercise(exercise: Exercise) { setActiveExercise(exercise); setActive(exercise.checkpoint); }
  function nextExercise(exercise: Exercise) { if (exercise.lessonId !== lesson.id) { navigate(`/lesson/${encodeURIComponent(exercise.lessonId)}?exercise=${encodeURIComponent(exercise.id)}`);return; } setStream(current => [...current.filter(e => e.id !== exercise.id), exercise]); openExercise(exercise); }
  function closePractice() { setActive(null); setActiveExercise(null); loadStream(); }
  async function startStream() {
    setStarting(true); setStreamError(null);
    try { const sitting = await api.openSitting(lesson.id);nextExercise(await api.nextExercise(lesson.id, null, null, sitting.id)); } catch (reason) { setStreamError(message(reason)); } finally { setStarting(false); }
  }
  useEffect(() => {
    if (window.location.hash) document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [lesson.id]);
  useEffect(() => {
    // After a stop, the learner is "back" once the film is on screen again (having left it): resume where it paused.
    const target = stopped ? films.current.get(stopped.filmId)?.element() : null;
    if (!stopped || !target) return;
    let left = false;
    const observer = new IntersectionObserver(entries => {
      const ratio = entries[entries.length - 1]!.intersectionRatio;
      if (ratio < .6) left = true;
      else if (left) { films.current.get(stopped.filmId)?.play(); setStopped(null); }
    }, { threshold: [0, .6] });
    observer.observe(target);
    return () => observer.disconnect();
  }, [stopped]);
  function actChanged(filmId: string, progress: ActProgress) {
    const next = reachedFilmCheckpoints(lesson.sections, filmBefore, filmId, progress);
    if (next.some(section => !unlocked.has(section.checkpoint.id))) {
      if (root.current) pauseEmbeds(root.current);
      setUnlocked(current => new Set([...current, ...next.map(section => section.checkpoint.id)]));
    }
  }
  const bridgeFor = (filmId: string): FilmBridge => ({
    register: handle => { if (handle) films.current.set(filmId, handle); else films.current.delete(filmId); },
    onChapters: links => setFilmLinks(current => ({ ...current, [filmId]: links })),
    onChapter: index => setNowPlaying({ filmId, index }),
    onStop: checkpointId => {
      const target = lesson.sections.find(section => section.kind === 'checkpoint' && section.checkpoint.id === checkpointId);
      if (!target) return;
      setUnlocked(current => current.has(checkpointId) ? current : new Set([...current, checkpointId]));
      setStopped({ filmId, checkpointId });
      scrollToCenter(document.getElementById(target.id));
    },
  });
  function watchPart(filmId: string, link: FilmLink) {
    const handle = films.current.get(filmId);
    if (!handle) return;
    setStopped(null);
    handle.seek(link.t);
    handle.play();
    scrollToCenter(handle.element());
  }
  function backToFilm() {
    const handle = stopped ? films.current.get(stopped.filmId) : undefined;
    setStopped(null);
    scrollToCenter(handle?.element());
    handle?.play();
  }
  const watch = new Map<string, { filmId: string; link: FilmLink; playing: boolean }>();
  for (const [filmId, links] of Object.entries(filmLinks)) for (const link of links) watch.set(link.sectionId, { filmId, link, playing: nowPlaying?.filmId === filmId && nowPlaying.index === link.index });
  return <><main className="lesson-page" data-format={format} ref={root} aria-hidden={active ? true : undefined} inert={active ? true : undefined}>
    <header className="lesson-header"><p className="kicker"><Link to={`/course/${lesson.courseId}`}>{lesson.kicker}</Link> · {lesson.minutes} min</p><h1>{lesson.title}</h1><p><Link className="button secondary" to={`/notes?lesson=${encodeURIComponent(lesson.id)}`}>Take notes</Link></p><p className="problem"><Rich html={lesson.problem} /></p></header>
    {progressError && <p className="error" role="alert">Your place could not be saved: {progressError}</p>}
    {lesson.sections.map((section, index) => <section className="lesson-section" id={section.id} data-index={index} key={section.id}>
      {section.kind === 'text' && <div className="reading">{section.title && <h2>{section.title}</h2>}{section.title && watch.has(section.id) && <WatchPart entry={watch.get(section.id)!} onWatch={watchPart} />}{section.paragraphs.map((paragraph, i) => <p key={i}><Rich html={paragraph} /></p>)}</div>}
      {section.kind === 'embed' && <><p className="kicker embed-label">{section.label}</p><EmbedSection embed={section.embed} label={section.label} onAct={progress => actChanged(section.id, progress)} />{(section.embed.frame || section.embed.options.caption !== section.caption) && <p className="section-caption"><Rich html={section.caption} /></p>}</>}
      {section.kind === 'video' && <VideoSection section={section} language={lesson.language} onAct={progress => actChanged(section.id, progress)} />}
      {section.kind === 'film' && <FilmSection section={section} sections={lesson.sections} onAct={progress => actChanged(section.id, progress)} bridge={bridgeFor(section.id)} />}
      {section.kind === 'diagram' && <><PeleDiagram source={section.mermaid} /><p className="section-caption"><Rich html={section.caption} /></p></>}
      {section.kind === 'code' && <figure className="lesson-figure"><pre className="lesson-code" tabIndex={0} aria-label={`${section.language} code example`}><code>{section.code}</code></pre>{section.caption !== null && <figcaption className="section-caption"><Rich html={section.caption} /></figcaption>}</figure>}
      {section.kind === 'figure' && <figure className="lesson-figure"><div className="lesson-figure-content" dangerouslySetInnerHTML={{ __html: section.html }} /><figcaption className="section-caption"><Rich html={section.caption} /></figcaption></figure>}
      {section.kind === 'think' && <details className="disclosure"><summary>You might think: <Rich html={section.claim} /></summary><div className="reading">{section.answer.map((paragraph, i) => <p key={i}><Rich html={paragraph} /></p>)}</div></details>}
      {section.kind === 'deep' && <details className="disclosure"><summary>{section.title}</summary><div className="reading">{section.body.map((paragraph, i) => <p key={i}><Rich html={paragraph} /></p>)}</div></details>}
      {section.kind === 'guess' && <GuessSection section={section} lessonId={lesson.id} />}
      {section.kind === 'checkpoint' && <div className={`checkpoint-card ${unlocked.has(section.checkpoint.id) ? '' : 'locked'}`}><p className="kicker">Pen checkpoint · {section.checkpoint.kind}{completed.has(section.checkpoint.id) ? ' · completed' : ''}</p><h2>{section.checkpoint.title}</h2><p>{section.checkpoint.prompt}</p><div className="actions"><button disabled={!unlocked.has(section.checkpoint.id)} onClick={() => { report(index); setActive(section.checkpoint); }}>{completed.has(section.checkpoint.id) ? 'Draw it again' : 'Open a page'} →</button>{!unlocked.has(section.checkpoint.id) && <span className="checkpoint-status">{filmBefore[section.id] ? section.afterAct === null ? 'Opens when the film ends' : `Opens after act ${section.afterAct + 1}` : 'Keep reading to open'}</span>}</div></div>}
    </section>)}
    <section className="lesson-section stream" aria-labelledby="stream-title"><p className="kicker">Keep going</p><h2 id="stream-title">Your exercises</h2>
      <p className="stream-intro">After any exercise, the tutor writes the next one from how it went: a variant, a step back, a stretch. Start here or from any checkpoint above, and stop whenever you like.</p>
      {stream.length > 0 && <ol className="stream-list">{stream.map((exercise, i) => <li key={exercise.id}><button type="button" className="stream-item" onClick={() => openExercise(exercise)}><span className="stream-number">{i + 1}</span><span><small>{exercise.move.replace('-', ' ')} · {exercise.status === 'done' ? 'done' : exercise.status === 'open' ? 'started' : 'new'}</small><strong>{exercise.checkpoint.title}</strong><span>{exercise.checkpoint.prompt}</span></span></button></li>)}</ol>}
      {streamError && <p className="error" role="alert">{streamError}</p>}
      <div className="actions"><button type="button" disabled={starting} onClick={() => void startStream()}>{starting ? 'Writing your exercise…' : stream.length ? 'Next exercise →' : 'Start the exercise stream →'}</button></div>
    </section>
    <footer className="lesson-section takeaway"><p className="kicker">Take this with you</p><div className="reading"><Rich html={lesson.takeaway} /></div><p className="actions"><Link className="button secondary" to={`/course/${lesson.courseId}`}>Back to the course →</Link></p></footer>
  </main>{stopped && !active && <button className="film-return" type="button" onClick={backToFilm}>↑ Back to the film</button>}{active && <PracticeLayer key={active.id} lesson={lesson} checkpoint={active} exercise={activeExercise} reviewId={active.id === reviewCheckpoint ? reviewId : null} onClose={closePractice} onNext={nextExercise} onDone={() => { if (!activeExercise) setCompleted(current => new Set([...current, active.id])); closePractice(); }} />}</>;
}

function scrollToCenter(element: Element | null | undefined) {
  element?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
}

function WatchPart({ entry: { filmId, link, playing }, onWatch }: { entry: { filmId: string; link: FilmLink; playing: boolean }; onWatch(filmId: string, link: FilmLink): void }) {
  const clock = `${Math.floor(link.t / 60)}:${String(Math.floor(link.t % 60)).padStart(2, '0')}`;
  return <p className="watch-part"><button className="secondary" type="button" aria-label={`Watch this part of the film: ${link.title}`} aria-current={playing ? 'true' : undefined} onClick={() => onWatch(filmId, link)}>{playing ? '▶ Playing' : '▶ Watch this part'} · {clock}</button></p>;
}
